import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Home, Loader2, RefreshCw, Search, Package,
  Calendar, MapPin, TrendingUp, TrendingDown, ExternalLink,
  CheckCircle2, X, AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("pr_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmt = (v, digits = 0) => (v == null || v === "" ? "—" :
  Number(v).toLocaleString(undefined, {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  }));

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString(undefined, {
    day: "2-digit", month: "short", year: "2-digit",
  }).replace(/ /g, "-");
};

const fmtRelativeAge = (isoStr) => {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

/**
 * MRP Purchase Proposals inbox — buyer worklist of MRP-generated proposals
 * from SAP BYD's ProcurementPlanningOrder BO. Displays each with the price
 * intelligence a buyer needs to source it: last PO price + standing list
 * prices across vendors. One-click "Convert to PR" pre-fills the existing
 * Create PR flow.
 *
 * URL: /mrp-proposals  (all users — buyers, requestors and approvers can read;
 * only Admin+ can trigger manual sync)
 */
export default function MRPProposals() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [sites, setSites] = useState([]);
  const [lastSynced, setLastSynced] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  // Filters
  const [site, setSite] = useState("");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [hideConverted, setHideConverted] = useState(true);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [criticallyOverdue, setCriticallyOverdue] = useState(false);
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [matCode, setMatCode] = useState("");
  const [deliveryBucket, setDeliveryBucket] = useState("");
  const [sourcingState, setSourcingState] = useState("");
  const [convertedByMe, setConvertedByMe] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [skip, setSkip] = useState(0);
  const limit = 50;

  // Detail drawer
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("pr_user") || "{}"); }
    catch { return {}; }
  }, []);
  const isAdmin = ["Admin", "IT_Admin", "Super_Admin"].some(r =>
    (user.role || "").toUpperCase().includes(r.toUpperCase()));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit, skip,
        hide_converted: hideConverted,
        overdue_only: overdueOnly,
        critically_overdue: criticallyOverdue,
        assigned_to_me: assignedToMe,
        converted_by_me: convertedByMe,
      });
      if (site) params.set("site_id", site);
      if (q) params.set("q", q);
      if (matCode) params.set("mat_code", matCode);
      if (deliveryBucket) params.set("delivery_bucket", deliveryBucket);
      if (sourcingState) params.set("sourcing_state", sourcingState);
      const r = await axios.get(`${API}/mrp-proposals?${params.toString()}`,
        { headers: authHeaders() });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
      setSites(r.data.sites || []);
      setLastSynced(r.data.last_synced);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load MRP proposals");
    } finally {
      setLoading(false);
    }
  }, [site, q, hideConverted, overdueOnly, criticallyOverdue, assignedToMe,
      convertedByMe, matCode, deliveryBucket, sourcingState, skip]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (objectId) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const r = await axios.get(`${API}/mrp-proposals/${objectId}`,
        { headers: authHeaders() });
      setDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load proposal");
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const runSync = async () => {
    if (!isAdmin) return;
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/admin/sap/sync/mrp-proposals`, {},
        { headers: authHeaders() });
      toast.success(`Synced ${r.data.rows_synced} proposals in ${(r.data.elapsed_ms / 1000).toFixed(1)}s`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const convertToPR = (proposal) => {
    const p = proposal;
    // Pre-fill Create PR via query params so the existing flow handles all
    // the actual PR creation logic. On save, that page also calls our
    // /mrp-proposals/{id}/mark-converted so the row grey-outs here.
    const params = new URLSearchParams({
      icode: p.product_id || "",
      qty: p.quantity || "",
      uom: p.unit_code || "",
      site: p.site_id || "",
      delivery: (p.delivery_datetime || "").slice(0, 10),
      mrp_proposal_id: p.object_id,
      remark: `MRP proposal #${p.proposal_id} — ${p.product_desc || ""}`.trim(),
    });
    nav(`/create?${params.toString()}`);
  };

  // ============ Split-Convert state ============
  // Buyer can switch between Single (existing flow — goes to CreatePR
  // pre-filled) and Split (allocation grid → server-side batch draft
  // creation with tier decision per row). Split state lives inside the
  // drawer so it resets whenever a new proposal is opened.
  const [convertMode, setConvertMode] = useState("single");
  const [splitRows, setSplitRows] = useState([]);
  const [splitPreview, setSplitPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [splitSubmitting, setSplitSubmitting] = useState(false);
  const [splitCommon, setSplitCommon] = useState({
    dept_id: "IT", material_group: "BOP", gst: "18",
    end_cust: "GENERAL", currency: "INR", exch_rate: 1,
    purc_type: "BOP", auth_medium: "Email", in_house: "No", extra_per: 0,
  });

  // Reset split state when the drawer closes / a new proposal opens.
  useEffect(() => {
    setConvertMode("single");
    setSplitRows([]);
    setSplitPreview(null);
  }, [selectedId]);

  // Prefill the split grid the first time buyer flips to Split — seeds
  // from top-3 standing list prices (cheapest first), with qty split
  // evenly. If fewer than 3 prices exist, uses what's available. Buyer
  // can add/remove rows freely afterwards.
  useEffect(() => {
    if (convertMode !== "split" || splitRows.length > 0 || !detail) return;
    const prices = detail.standing_list_prices || [];
    const top = prices.slice(0, 3);
    const qty = Number(detail.proposal.quantity || 0);
    const rowCount = top.length || 1;
    const each = qty / rowCount;
    const seeded = top.length
      ? top.map((p, i) => ({
          supplier_code: p.supplier_code, supplier_name: p.supplier_name,
          qty: i === rowCount - 1 ? qty - each * (rowCount - 1) : each,
          rate: p.amount, currency: p.currency_code || "INR",
          unit_code: p.unit_code || detail.proposal.unit_code,
          delivery_date: (detail.proposal.delivery_datetime || "").slice(0, 10),
          override_reason: "",
        }))
      : [{
          supplier_code: "", supplier_name: "", qty, rate: 0,
          currency: "INR", unit_code: detail.proposal.unit_code,
          delivery_date: (detail.proposal.delivery_datetime || "").slice(0, 10),
          override_reason: "",
        }];
    setSplitRows(seeded);
  }, [convertMode, detail, splitRows.length]);

  // Debounced live preview — re-fires whenever grid changes so buyer
  // sees the tier/list-price/anomaly badge per row in real time.
  useEffect(() => {
    if (convertMode !== "split" || splitRows.length === 0 || !selectedId) return;
    const invalid = splitRows.some(r => !r.supplier_code || !(Number(r.qty) > 0) || !(Number(r.rate) > 0));
    if (invalid) { setSplitPreview(null); return; }
    const handle = setTimeout(async () => {
      setPreviewing(true);
      try {
        const r = await axios.post(
          `${API}/mrp-proposals/${selectedId}/split-preview`,
          { allocations: splitRows.map(x => ({
              supplier_code: x.supplier_code,
              supplier_name: x.supplier_name,
              qty: Number(x.qty), rate: Number(x.rate),
              currency: x.currency, unit_code: x.unit_code,
              delivery_date: x.delivery_date || null,
              override_reason: x.override_reason || null,
            })) },
          { headers: authHeaders() },
        );
        setSplitPreview(r.data);
      } catch (e) {
        setSplitPreview({ ok: false, validation: {
          errors: [e.response?.data?.detail || "Preview failed"] }, allocations: [] });
      } finally {
        setPreviewing(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [splitRows, convertMode, selectedId]);

  const updateRow = (idx, patch) => {
    setSplitRows(rs => rs.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };
  const addRow = () => {
    setSplitRows(rs => [...rs, {
      supplier_code: "", supplier_name: "", qty: 0, rate: 0,
      currency: "INR", unit_code: detail?.proposal.unit_code || "",
      delivery_date: (detail?.proposal.delivery_datetime || "").slice(0, 10),
      override_reason: "",
    }]);
  };
  const removeRow = (idx) => setSplitRows(rs => rs.filter((_, i) => i !== idx));

  const submitSplit = async () => {
    if (!splitPreview?.ok) {
      toast.error("Fix validation errors before submitting");
      return;
    }
    setSplitSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/mrp-proposals/${selectedId}/split-convert`,
        {
          allocations: splitRows.map(x => ({
            supplier_code: x.supplier_code, supplier_name: x.supplier_name,
            qty: Number(x.qty), rate: Number(x.rate),
            currency: x.currency, unit_code: x.unit_code,
            delivery_date: x.delivery_date || null,
            override_reason: x.override_reason || null,
          })),
          ...splitCommon,
        },
        { headers: authHeaders() },
      );
      const n = r.data.drafts.length;
      const eligibleN = r.data.drafts.filter(d => d.auto_approve_eligible).length;
      toast.success(
        r.data.shadow_mode
          ? `Created ${n} draft PRs · ${eligibleN} would auto-approve (shadow mode — still going through L1/L2)`
          : `Created ${n} draft PRs · ${eligibleN} auto-approved`
      );
      setSelectedId(null);
      await load();
      // Redirect to My PRs so buyer can review + submit each draft.
      setTimeout(() => nav("/my-prs"), 800);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.validation_failed) {
        toast.error(`Validation: ${detail.validation_failed.join(" · ")}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Split-convert failed");
      }
    } finally {
      setSplitSubmitting(false);
    }
  };

  const overdueDays = (deliveryIso) => {
    if (!deliveryIso) return null;
    const d = new Date(deliveryIso);
    const days = Math.floor((Date.now() - d.getTime()) / (24 * 3600 * 1000));
    return days > 0 ? days : null;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost" size="sm"
              onClick={() => nav("/sap")}
              data-testid="mrp-back-sap"
            >
              <ArrowLeft size={16} className="mr-1" /> SAP Workspace
            </Button>
            <Button
              variant="ghost" size="sm"
              onClick={() => nav("/home")}
              data-testid="mrp-back-home"
            >
              <Home size={16} className="mr-1" /> Home
            </Button>
            <div className="ml-2">
              <h1 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
                <Package size={18} className="text-indigo-700" />
                MRP Purchase Proposals
              </h1>
              <div className="text-xs text-slate-500">
                {total.toLocaleString()} actionable · last synced {fmtRelativeAge(lastSynced) || "never"}
                {lastSynced && ` (${fmtDate(lastSynced)})`}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                variant="outline" size="sm"
                onClick={runSync}
                disabled={syncing}
                data-testid="mrp-sync-btn"
              >
                {syncing ? <Loader2 size={14} className="animate-spin mr-1" />
                         : <RefreshCw size={14} className="mr-1" />}
                Sync from SAP
              </Button>
            )}
          </div>
        </div>

        {/* Filter bar */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-3 flex flex-wrap items-center gap-2">
          <form
            onSubmit={(e) => { e.preventDefault(); setSkip(0); setQ(qInput.trim()); }}
            className="flex items-center gap-1 flex-1 min-w-[200px]"
          >
            <div className="relative flex-1">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search product code or description…"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                className="pl-8 h-8 text-sm"
                data-testid="mrp-search-input"
              />
            </div>
            <Button type="submit" size="sm" variant="secondary"
                    data-testid="mrp-search-btn">Search</Button>
            {q && (
              <Button type="button" size="sm" variant="ghost"
                      onClick={() => { setQ(""); setQInput(""); setSkip(0); }}
                      data-testid="mrp-search-clear">
                <X size={14} />
              </Button>
            )}
          </form>

          <select
            value={site}
            onChange={(e) => { setSkip(0); setSite(e.target.value); }}
            className="h-8 text-sm border border-slate-300 rounded px-2"
            data-testid="mrp-site-filter"
          >
            <option value="">All sites ({sites.length})</option>
            {sites.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          <label className="flex items-center gap-1 text-xs text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={hideConverted}
              onChange={(e) => { setSkip(0); setHideConverted(e.target.checked); }}
              data-testid="mrp-hide-converted"
            />
            Hide converted
          </label>

          <label className="flex items-center gap-1 text-xs text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={overdueOnly}
              onChange={(e) => { setSkip(0); setOverdueOnly(e.target.checked); }}
              data-testid="mrp-overdue-only"
            />
            Overdue only
          </label>

          <label className="flex items-center gap-1 text-xs text-red-700 cursor-pointer font-semibold">
            <input
              type="checkbox"
              checked={criticallyOverdue}
              onChange={(e) => { setSkip(0); setCriticallyOverdue(e.target.checked); }}
              data-testid="mrp-critically-overdue"
            />
            ⚠ Critical (&gt;30d)
          </label>

          <label className="flex items-center gap-1 text-xs text-indigo-700 cursor-pointer font-semibold">
            <input
              type="checkbox"
              checked={assignedToMe}
              onChange={(e) => { setSkip(0); setAssignedToMe(e.target.checked); }}
              data-testid="mrp-assigned-to-me"
            />
            👤 Assigned to me
          </label>

          <Button variant="ghost" size="sm"
                  onClick={() => setShowAdvanced(v => !v)}
                  className="text-xs h-7"
                  data-testid="mrp-toggle-advanced">
            {showAdvanced ? "− Fewer" : "+ More filters"}
          </Button>
        </div>

        {showAdvanced && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-3 border-t border-slate-100 pt-2 flex flex-wrap items-center gap-2">
            <select
              value={deliveryBucket}
              onChange={(e) => { setSkip(0); setDeliveryBucket(e.target.value); }}
              className="h-8 text-sm border border-slate-300 rounded px-2"
              data-testid="mrp-delivery-bucket"
            >
              <option value="">Any delivery</option>
              <option value="overdue">Overdue</option>
              <option value="week">Within 7 days</option>
              <option value="month">Within 30 days</option>
              <option value="next_month">30–60 days out</option>
              <option value="later">60+ days out</option>
            </select>

            <select
              value={sourcingState}
              onChange={(e) => { setSkip(0); setSourcingState(e.target.value); }}
              className="h-8 text-sm border border-slate-300 rounded px-2"
              data-testid="mrp-sourcing-state"
            >
              <option value="">Any sourcing</option>
              <option value="has_list_price">💰 Has list price</option>
              <option value="has_last_po">📜 Has PO history</option>
              <option value="cold">❄ Cold (need quote)</option>
            </select>

            <Input
              placeholder="Material code (e.g. 57)"
              value={matCode}
              onChange={(e) => { setSkip(0); setMatCode(e.target.value.trim()); }}
              className="h-8 text-sm w-40"
              data-testid="mrp-mat-code"
            />

            <label className="flex items-center gap-1 text-xs text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={convertedByMe}
                onChange={(e) => { setSkip(0); setConvertedByMe(e.target.checked); }}
                data-testid="mrp-converted-by-me"
              />
              My conversions only
            </label>

            {(deliveryBucket || sourcingState || matCode || convertedByMe || criticallyOverdue) && (
              <Button variant="ghost" size="sm"
                      onClick={() => {
                        setDeliveryBucket(""); setSourcingState(""); setMatCode("");
                        setConvertedByMe(false); setCriticallyOverdue(false); setSkip(0);
                      }}
                      className="text-xs h-7 text-red-600"
                      data-testid="mrp-clear-advanced">
                Clear advanced
              </Button>
            )}
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="animate-spin text-indigo-600" size={30} />
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-sm p-10 text-center">
            <Package size={40} className="mx-auto text-slate-300 mb-3" />
            <div className="text-sm text-slate-600">
              No proposals match your filters.
              {hideConverted && total === 0 && " Try un-checking Hide Converted."}
            </div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-700 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left">Proposal</th>
                    <th className="px-3 py-2 text-left">Product</th>
                    <th className="px-3 py-2 text-left">Site</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-left">Delivery</th>
                    <th className="px-3 py-2 text-left">Age</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => {
                    const overdue = overdueDays(r.delivery_datetime);
                    return (
                      <tr
                        key={r.object_id}
                        className={`hover:bg-indigo-50 cursor-pointer transition-colors
                          ${r.converted ? "opacity-60 bg-emerald-50" : ""}`}
                        onClick={() => setSelectedId(r.object_id)}
                        data-testid={`mrp-row-${r.proposal_id}`}
                      >
                        <td className="px-3 py-2 font-mono text-xs text-slate-600">
                          #{r.proposal_id}
                          {r.converted && (
                            <div className="text-[10px] text-emerald-700 font-sans font-semibold mt-0.5">
                              ✓ PR#{r.converted_voc_no}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-mono text-xs font-semibold text-slate-900">
                            {r.product_id || "—"}
                          </div>
                          <div className="text-xs text-slate-600 truncate max-w-[240px]">
                            {r.product_desc || <span className="text-slate-400">no description</span>}
                          </div>
                          <div className="mt-0.5 flex items-center gap-1 flex-wrap">
                            {r.mat_type_name && (
                              <span className="text-[9px] px-1 py-0 bg-slate-100 rounded text-slate-600 font-medium">
                                {r.mat_type_name}
                              </span>
                            )}
                            {r.sourcing_state === "has_list_price" && (
                              <span className="text-[9px] px-1 py-0 bg-emerald-100 text-emerald-800 rounded font-medium" title="Standing list price available">💰 list</span>
                            )}
                            {r.sourcing_state === "has_last_po" && (
                              <span className="text-[9px] px-1 py-0 bg-blue-100 text-blue-800 rounded font-medium" title="Has purchase history">📜 PO</span>
                            )}
                            {r.sourcing_state === "cold" && (
                              <span className="text-[9px] px-1 py-0 bg-amber-100 text-amber-800 rounded font-medium" title="No list price or PO history — quote needed">❄ cold</span>
                            )}
                            {r.is_assigned_to_me && (
                              <span className="text-[9px] px-1 py-0 bg-indigo-600 text-white rounded font-bold" title="Assigned to you">👤 mine</span>
                            )}
                            {r.critically_overdue && (
                              <span className="text-[9px] px-1 py-0 bg-red-600 text-white rounded font-bold" title=">30 days past due">🚨 crit</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 rounded text-xs font-mono">
                            <MapPin size={10} /> {r.site_id || "—"}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right font-mono">
                          {fmt(r.quantity, 0)} <span className="text-slate-400 text-xs">{r.unit_code}</span>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <div className="flex items-center gap-1">
                            <Calendar size={11} className={overdue ? "text-red-600" : "text-slate-500"} />
                            <span className={overdue ? "text-red-700 font-semibold" : ""}>
                              {fmtDate(r.delivery_datetime)}
                            </span>
                          </div>
                          {overdue && (
                            <div className="text-[10px] text-red-600 mt-0.5">
                              {overdue}d overdue
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">
                          {fmtRelativeAge(r.last_change_datetime)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {r.converted ? (
                            <Button size="sm" variant="outline" disabled>
                              <CheckCircle2 size={13} className="mr-1 text-emerald-600" />
                              Converted
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); convertToPR(r); }}
                              className="bg-indigo-700 hover:bg-indigo-800"
                              data-testid={`mrp-convert-${r.proposal_id}`}
                            >
                              Convert → PR
                              <ExternalLink size={12} className="ml-1" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > limit && (
              <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-between text-xs text-slate-600">
                <div>
                  Showing {skip + 1}–{Math.min(skip + rows.length, total)} of {total.toLocaleString()}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm" variant="outline" disabled={skip === 0}
                    onClick={() => setSkip(Math.max(0, skip - limit))}
                    data-testid="mrp-prev-page"
                  >Prev</Button>
                  <Button
                    size="sm" variant="outline"
                    disabled={skip + rows.length >= total}
                    onClick={() => setSkip(skip + limit)}
                    data-testid="mrp-next-page"
                  >Next</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Detail drawer */}
      {selectedId && (
        <div
          className="fixed inset-0 bg-black/40 z-30 flex justify-end"
          onClick={() => setSelectedId(null)}
          data-testid="mrp-detail-backdrop"
        >
          <div
            className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package size={18} className="text-indigo-700" />
                <h2 className="text-base font-bold">Proposal Detail</h2>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedId(null)}
                      data-testid="mrp-detail-close">
                <X size={16} />
              </Button>
            </div>

            {detailLoading || !detail ? (
              <div className="p-10 flex items-center justify-center">
                <Loader2 size={26} className="animate-spin text-indigo-600" />
              </div>
            ) : (
              <div className="p-5 space-y-5">
                {/* Proposal header */}
                <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-3">
                  <div className="flex items-baseline justify-between mb-1">
                    <div className="font-mono text-xs text-indigo-800">
                      MRP #{detail.proposal.proposal_id}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-indigo-600 font-bold">
                      Status {detail.proposal.lifecycle_status === "1" ? "Not Started" :
                              detail.proposal.lifecycle_status === "2" ? "In Process" :
                              detail.proposal.lifecycle_status}
                    </div>
                  </div>
                  <div className="font-mono text-sm font-semibold">{detail.proposal.product_id}</div>
                  <div className="text-sm text-slate-800">{detail.proposal.product_desc}</div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <div className="text-slate-500">Site</div>
                      <div className="font-semibold">{detail.proposal.site_id || "—"}</div>
                    </div>
                    <div>
                      <div className="text-slate-500">Quantity</div>
                      <div className="font-semibold">
                        {fmt(detail.proposal.quantity, 2)} {detail.proposal.unit_code}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-500">Delivery</div>
                      <div className="font-semibold">{fmtDate(detail.proposal.delivery_datetime)}</div>
                    </div>
                  </div>
                </div>

                {/* Cheapest suggestion highlight */}
                {(detail.cheapest_standing_price || detail.cheapest_last_po) && (
                  <div className="border-l-4 border-emerald-500 bg-emerald-50 rounded-sm px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-0.5">
                      AI Cheaper-Vendor Suggestion
                    </div>
                    {detail.cheapest_standing_price ? (
                      <div className="text-sm">
                        <span className="font-semibold">
                          {detail.cheapest_standing_price.supplier_name || detail.cheapest_standing_price.supplier_code}
                        </span>
                        {" — "}
                        <span className="font-mono">
                          {fmt(detail.cheapest_standing_price.amount, 2)} {detail.cheapest_standing_price.currency_code}
                          /{detail.cheapest_standing_price.unit_code}
                        </span>
                        <div className="text-xs text-slate-600 mt-0.5">
                          Cheapest currently-valid list price
                        </div>
                      </div>
                    ) : detail.cheapest_last_po && (
                      <div className="text-sm">
                        <span className="font-semibold">
                          {detail.cheapest_last_po.supplier_name || detail.cheapest_last_po.Sup_code}
                        </span>
                        {" — "}
                        <span className="font-mono">₹{fmt(detail.cheapest_last_po.Rate, 2)}</span>
                        <div className="text-xs text-slate-600 mt-0.5">
                          Last PO on {fmtDate(detail.cheapest_last_po.Vdate)}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Standing list prices */}
                {detail.standing_list_prices.length > 0 && (
                  <div>
                    <div className="text-xs font-bold text-slate-700 mb-1 flex items-center gap-1">
                      <TrendingDown size={12} className="text-emerald-600" />
                      Standing List Prices ({detail.standing_list_prices.length})
                    </div>
                    <div className="border border-slate-200 rounded-sm overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="px-2 py-1 text-left">Supplier</th>
                            <th className="px-2 py-1 text-right">Rate</th>
                            <th className="px-2 py-1 text-left">Valid</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {detail.standing_list_prices.slice(0, 8).map((r, i) => (
                            <tr key={i} className={i === 0 ? "bg-emerald-50" : ""}>
                              <td className="px-2 py-1">
                                <div className="font-mono text-[10px] text-slate-500">
                                  {r.supplier_code}
                                </div>
                                <div>{r.supplier_name || "—"}</div>
                              </td>
                              <td className="px-2 py-1 text-right font-mono">
                                {fmt(r.amount, 2)} {r.currency_code}/{r.unit_code}
                              </td>
                              <td className="px-2 py-1 text-slate-600">
                                {fmtDate(r.valid_from)}
                                {r.valid_to && ` → ${fmtDate(r.valid_to)}`}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Last PO history */}
                {detail.last_po_history.length > 0 && (
                  <div>
                    <div className="text-xs font-bold text-slate-700 mb-1 flex items-center gap-1">
                      <TrendingUp size={12} className="text-blue-600" />
                      Last PO History ({detail.last_po_history.length})
                    </div>
                    <div className="border border-slate-200 rounded-sm overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="px-2 py-1 text-left">Voc#</th>
                            <th className="px-2 py-1 text-left">Date</th>
                            <th className="px-2 py-1 text-left">Supplier</th>
                            <th className="px-2 py-1 text-right">Rate</th>
                            <th className="px-2 py-1 text-right">Qty</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {detail.last_po_history.map((r) => (
                            <tr key={r.Voc_no}>
                              <td className="px-2 py-1 font-mono">{r.Voc_no}</td>
                              <td className="px-2 py-1">{fmtDate(r.Vdate)}</td>
                              <td className="px-2 py-1">
                                <div className="font-mono text-[10px] text-slate-500">
                                  {r.Sup_code}
                                </div>
                                <div className="truncate max-w-[140px]">{r.supplier_name || "—"}</div>
                              </td>
                              <td className="px-2 py-1 text-right font-mono">
                                ₹{fmt(r.Rate, 2)}
                              </td>
                              <td className="px-2 py-1 text-right font-mono">
                                {fmt(r.Qty, 0)} {r.Uom}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {detail.last_po_history.length === 0 && detail.standing_list_prices.length === 0 && (
                  <div className="border border-amber-200 bg-amber-50 rounded-sm px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
                    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                    No historical PO or standing list price found for this material.
                    Buyer will need to solicit a fresh quote before converting.
                  </div>
                )}

                {/* Convert Mode Toggle */}
                <div className="flex gap-1 border-b border-slate-200 -mx-5 px-5 pb-2">
                  <button
                    onClick={() => setConvertMode("single")}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-t-sm transition-colors
                      ${convertMode === "single"
                        ? "bg-indigo-700 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                    data-testid="mrp-mode-single"
                  >
                    Single Vendor
                  </button>
                  <button
                    onClick={() => setConvertMode("split")}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-t-sm transition-colors
                      ${convertMode === "split"
                        ? "bg-indigo-700 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                    data-testid="mrp-mode-split"
                  >
                    Split-Convert (multi-vendor)
                  </button>
                </div>

                {/* Split Grid — only in split mode */}
                {convertMode === "split" && (
                  <div className="space-y-3">
                    <div className="text-xs text-slate-600 flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-700">Allocation</span>
                      <span>Split the {fmt(detail.proposal.quantity, 0)} {detail.proposal.unit_code} across vendors.</span>
                      <span className="text-slate-400">Sum tolerance ±2%, no vendor &gt; 80%.</span>
                    </div>

                    {/* Split rows */}
                    <div className="border border-slate-200 rounded-sm overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="px-2 py-1 text-left">Supplier</th>
                            <th className="px-2 py-1 text-right">Qty</th>
                            <th className="px-2 py-1 text-right">Rate</th>
                            <th className="px-2 py-1 text-left">Delivery</th>
                            <th className="px-2 py-1 text-left">Tier</th>
                            <th className="px-2 py-1"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {splitRows.map((row, idx) => {
                            const preview = splitPreview?.allocations?.[idx];
                            const tier = preview?.tier?.tier;
                            const overridden = row.override_reason?.trim();
                            return (
                              <tr key={idx} className={`hover:bg-slate-50 ${tier === "TIERLESS" ? "bg-emerald-50" : ""}`}>
                                <td className="px-2 py-1">
                                  <Input
                                    value={row.supplier_code}
                                    onChange={e => updateRow(idx, { supplier_code: e.target.value.toUpperCase() })}
                                    placeholder="Code"
                                    className="h-7 text-xs font-mono w-20 mb-0.5"
                                    data-testid={`split-supplier-${idx}`}
                                  />
                                  <Input
                                    value={row.supplier_name || ""}
                                    onChange={e => updateRow(idx, { supplier_name: e.target.value })}
                                    placeholder="Name"
                                    className="h-6 text-[10px] w-32"
                                  />
                                </td>
                                <td className="px-2 py-1">
                                  <Input
                                    type="number" step="any" min="0"
                                    value={row.qty}
                                    onChange={e => updateRow(idx, { qty: e.target.value })}
                                    className="h-7 text-xs text-right w-24"
                                    data-testid={`split-qty-${idx}`}
                                  />
                                </td>
                                <td className="px-2 py-1">
                                  <Input
                                    type="number" step="any" min="0"
                                    value={row.rate}
                                    onChange={e => updateRow(idx, { rate: e.target.value })}
                                    className="h-7 text-xs text-right w-20"
                                    data-testid={`split-rate-${idx}`}
                                  />
                                </td>
                                <td className="px-2 py-1">
                                  <Input
                                    type="date"
                                    value={row.delivery_date}
                                    onChange={e => updateRow(idx, { delivery_date: e.target.value })}
                                    className="h-7 text-xs w-32"
                                    data-testid={`split-delivery-${idx}`}
                                  />
                                </td>
                                <td className="px-2 py-1">
                                  {previewing && !preview ? (
                                    <Loader2 size={12} className="animate-spin text-slate-400" />
                                  ) : preview ? (
                                    <div>
                                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold
                                        ${tier === "TIERLESS" ? "bg-emerald-500 text-white"
                                          : tier === "L1_ONLY" ? "bg-amber-400 text-slate-900"
                                          : "bg-slate-400 text-white"}`}
                                        data-testid={`split-tier-${idx}`}>
                                        {tier === "L1_L2" ? "L1+L2" : tier === "L1_ONLY" ? "L1" : "AUTO"}
                                      </span>
                                      <div className="text-[10px] text-slate-500 mt-0.5">
                                        {preview.list_price_validated ? "✓ list-price" : "spot"}
                                      </div>
                                      {preview.anomaly_flags && Object.entries(preview.anomaly_flags).filter(([,v]) => v).map(([k]) => (
                                        <div key={k} className="text-[9px] text-red-600" title={k}>
                                          ⚠ {k.replace("_"," ")}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-slate-400 text-[10px]">—</span>
                                  )}
                                </td>
                                <td className="px-2 py-1 text-right">
                                  {splitRows.length > 1 && (
                                    <Button variant="ghost" size="sm"
                                            onClick={() => removeRow(idx)}
                                            className="h-6 w-6 p-0"
                                            data-testid={`split-remove-${idx}`}>
                                      <X size={12} className="text-red-600" />
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Concentration override reason — only for rows over cap */}
                    {splitPreview?.allocations?.some((a, i) => {
                      const total = splitPreview.total_amount_inr;
                      const pct = total > 0 ? (a.amount_inr / total * 100) : 0;
                      return pct > 80 && !splitRows[i]?.override_reason?.trim();
                    }) && (
                      <div className="bg-amber-50 border border-amber-300 rounded-sm p-2">
                        <div className="text-xs font-bold text-amber-900 mb-1">
                          ⚠ One row exceeds 80% concentration — provide override reason:
                        </div>
                        {splitRows.map((r, i) => {
                          const preview = splitPreview?.allocations?.[i];
                          const total = splitPreview?.total_amount_inr || 0;
                          const pct = total > 0 ? (preview?.amount_inr / total * 100) : 0;
                          if (pct <= 80) return null;
                          return (
                            <Input
                              key={i}
                              value={r.override_reason}
                              onChange={e => updateRow(i, { override_reason: e.target.value })}
                              placeholder={`Reason for ${r.supplier_code || `row ${i+1}`} at ${pct.toFixed(0)}% concentration`}
                              className="h-7 text-xs mb-1"
                              data-testid={`split-override-${i}`}
                            />
                          );
                        })}
                      </div>
                    )}

                    <Button
                      variant="outline" size="sm" onClick={addRow}
                      className="w-full h-7 text-xs"
                      data-testid="split-add-row"
                    >
                      + Add vendor row
                    </Button>

                    {/* Validation panel */}
                    {splitPreview && (
                      <div className={`rounded-sm p-2 text-xs
                        ${splitPreview.ok
                          ? "bg-emerald-50 border border-emerald-300 text-emerald-900"
                          : "bg-red-50 border border-red-300 text-red-900"}`}>
                        <div className="font-bold flex items-center gap-1">
                          {splitPreview.ok ? <><CheckCircle2 size={12}/> Validation OK</> : <><AlertTriangle size={12}/> Fix these:</>}
                        </div>
                        {splitPreview.validation.errors.map((e, i) => (
                          <div key={i} className="ml-4">• {e}</div>
                        ))}
                        {splitPreview.validation.warnings?.map((w, i) => (
                          <div key={`w${i}`} className="ml-4 text-amber-800">• {w}</div>
                        ))}
                        <div className="mt-1 pt-1 border-t border-emerald-200 flex justify-between font-mono">
                          <span>Sum qty: {fmt(splitPreview.validation.total_qty, 2)} / {fmt(detail.proposal.quantity, 2)} {detail.proposal.unit_code}</span>
                          <span>Total: ₹{fmt(splitPreview.total_amount_inr, 2)}</span>
                        </div>
                      </div>
                    )}

                    {/* Shared classification fields (common across all N drafts) */}
                    <details className="border border-slate-200 rounded-sm">
                      <summary className="px-3 py-2 text-xs font-bold text-slate-700 cursor-pointer select-none hover:bg-slate-50">
                        Shared PR fields (applied to all N drafts)
                      </summary>
                      <div className="p-3 grid grid-cols-2 gap-2 text-xs">
                        <label>Dept
                          <Input value={splitCommon.dept_id}
                                 onChange={e => setSplitCommon({...splitCommon, dept_id: e.target.value})}
                                 className="h-7 text-xs" data-testid="split-common-dept" />
                        </label>
                        <label>Mat Group
                          <Input value={splitCommon.material_group}
                                 onChange={e => setSplitCommon({...splitCommon, material_group: e.target.value})}
                                 className="h-7 text-xs" />
                        </label>
                        <label>GST %
                          <Input value={splitCommon.gst}
                                 onChange={e => setSplitCommon({...splitCommon, gst: e.target.value})}
                                 className="h-7 text-xs" />
                        </label>
                        <label>End User
                          <Input value={splitCommon.end_cust}
                                 onChange={e => setSplitCommon({...splitCommon, end_cust: e.target.value})}
                                 className="h-7 text-xs" />
                        </label>
                        <label>Purc Type
                          <Input value={splitCommon.purc_type}
                                 onChange={e => setSplitCommon({...splitCommon, purc_type: e.target.value})}
                                 className="h-7 text-xs" />
                        </label>
                        <label>Auth Medium
                          <Input value={splitCommon.auth_medium}
                                 onChange={e => setSplitCommon({...splitCommon, auth_medium: e.target.value})}
                                 className="h-7 text-xs" />
                        </label>
                      </div>
                    </details>
                  </div>
                )}

                {/* Action */}
                <div className="sticky bottom-0 -mx-5 -mb-5 px-5 py-3 bg-white border-t border-slate-200">
                  {detail.proposal.converted ? (
                    <Button variant="outline" disabled className="w-full">
                      <CheckCircle2 size={16} className="mr-2 text-emerald-600" />
                      Already converted to PR{detail.proposal.converted_voc_nos?.length > 1 ? "s" : ""}
                      {" "}
                      {(detail.proposal.converted_voc_nos || []).map(v => `#${v}`).join(", ") || `#${detail.proposal.converted_voc_no}`}
                    </Button>
                  ) : convertMode === "single" ? (
                    <Button
                      className="w-full bg-indigo-700 hover:bg-indigo-800"
                      onClick={() => convertToPR(detail.proposal)}
                      data-testid="mrp-detail-convert-btn"
                    >
                      Convert to PR (opens Create PR pre-filled)
                      <ExternalLink size={14} className="ml-2" />
                    </Button>
                  ) : (
                    <Button
                      className="w-full bg-indigo-700 hover:bg-indigo-800"
                      onClick={submitSplit}
                      disabled={splitSubmitting || !splitPreview?.ok || previewing}
                      data-testid="mrp-detail-split-submit"
                    >
                      {splitSubmitting
                        ? <><Loader2 size={16} className="mr-2 animate-spin"/> Creating drafts…</>
                        : <>Create {splitRows.length} Draft PRs → Review in My PRs<ExternalLink size={14} className="ml-2" /></>}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
